import type { CheckboxMap, Filters, LngLat, MapBounds, OpenNumberRange, RoomWithPeople, SortOption } from '@/interfaces';
import { formatISODate, isFutureRange, safeDifferenceInCalendarDays } from 'shared/util/datetime-fns';

const composedCheckboxMapToQuery = (map: CheckboxMap, key: string, separator: string): readonly string[] =>
    Object.keys(map)
        .filter((k) => map[k])
        .flatMap((composed: string) => composed.split(separator).map((value) => `${key}=${encodeURIComponent(value)}`));

const checkboxMapToQuery = (map: CheckboxMap, key: string): readonly string[] =>
    Object.keys(map)
        .filter((k) => map[k])
        .map((value) => `${key}=${encodeURIComponent(value)}`);

const getHotelIdsFromFavorites = (map: CheckboxMap): readonly number[] =>
    Object.keys(map)
        .filter((key) => map[key])
        .flatMap((value: string) => value.split(',').map((val) => Number.parseInt(val)))
        .filter((value, index, self) => self.indexOf(value) === index);

const listToQuery = <T>(list: readonly T[], key: string): readonly string[] =>
    list.length > 0 ? [`${key}=${encodeURIComponent(list.map((item) => `${item}`).join('|'))}`] : [];

const priceToQuery = (price: OpenNumberRange): readonly string[] =>
    price ? [`minPrice=${price[0]}`].concat(price[1] ? [`maxPrice=${price[1]}`] : []) : [];

const sortToQuery = (sort?: SortOption): readonly string[] => (sort ? [`sortField=${sort.value}`, `sortDirection=${sort.direction}`] : []);

const pageToQuery = (page?: number): readonly string[] => (page ? [`pageIndex=${page - 1}`] : []);

const pageSizeToQuery = (pageSize?: number): readonly string[] => (pageSize ? [`pageSize=${pageSize}`] : []);

const isNumber = (value: string | number): boolean => !Number.isNaN(Number(value));

const hotelToQuery = (value?: string): readonly string[] => (value ? [`hotel=${isNumber(value) ? 'khotel' : 'hotel'}:${value}`] : []);

const roomsToQuery = (rooms: readonly RoomWithPeople[]): readonly string[] =>
    rooms.length > 0
        ? [
              `rooms=${encodeURIComponent(
                  rooms
                      .map((room) => (room.children.length > 0 ? `${room.adults}:${room.children.join(',')}` : `${room.adults}`))
                      .join('|'),
              )}`,
          ]
        : [];

const coordinatesToString = (coordinates: LngLat): string => `${coordinates[1]},${coordinates[0]}`;

const mapBoundsToQuery = (bounds?: MapBounds) =>
    bounds
        ? [
              `destination=polygon:${[bounds.sw, [bounds.sw[0], bounds.ne[1]], bounds.ne, [bounds.ne[0], bounds.sw[1]], bounds.sw]
                  .map(coordinatesToString)
                  .join(';')}:999`,
          ]
        : [];

const adjustHotelIds = (hotelIds: readonly number[], favoriteHotels: CheckboxMap): readonly number[] =>
    checkboxMapToQuery(favoriteHotels, '').length > 0 ? getHotelIdsFromFavorites(favoriteHotels) : hotelIds;

const hotelIdsToQuery = (hotelIds: readonly number[] = []): readonly string[] => {
    return hotelIds.length > 0 ? [`destination=${isNumber(hotelIds[0]) ? 'khotels' : 'hotels'}:${hotelIds.join(',')}`] : [];
};

const destinationToQuery = (destination?: string, hotelsIds: readonly number[] = [], bounds?: MapBounds): readonly string[] =>
    destination && hotelsIds.length === 0 && !bounds ? [`destination=${isNumber(destination) ? 'kplace' : 'place'}:${destination}`] : [];

const hotelNameToQuery = (value?: string): readonly string[] => (value ? [`hotelName=${encodeURIComponent(value)}`] : []);

const checkInCheckOutToQuery = (checkInDate?: Date | number, checkOutDate?: Date | number) =>
    isFutureRange(checkOutDate, checkInDate) && safeDifferenceInCalendarDays(checkInDate, checkOutDate) <= 30
        ? [`checkin=${formatISODate(checkInDate)}`, `checkout=${formatISODate(checkOutDate)}`]
        : [];

export const filtersToHotelsApiQueryParams = (filters: Filters, pageSize?: number): string =>
    []
        .concat(destinationToQuery(filters.destination, filters.hotelIds, filters.mapBounds))
        .concat(hotelIdsToQuery(adjustHotelIds(filters.hotelIds, filters.favoriteHotels)))
        .concat(mapBoundsToQuery(filters.mapBounds))
        .concat(checkInCheckOutToQuery(filters.checkInDate, filters.checkOutDate))
        .concat(roomsToQuery(filters.rooms))
        .concat(sortToQuery(filters.sort))
        .concat(pageToQuery(filters.page))
        .concat(pageSizeToQuery(pageSize))
        .concat(checkboxMapToQuery(filters.starRating, 'starRating'))
        .concat(checkboxMapToQuery(filters.guestRating, 'guestRatings'))
        .concat(checkboxMapToQuery(filters.hotelChain, 'chains'))
        .concat(checkboxMapToQuery(filters.facility, 'features'))
        .concat(composedCheckboxMapToQuery(filters.propertyType, 'propertyTypes', '|'))
        .concat(hotelNameToQuery(filters.hotelName))
        .concat(priceToQuery(filters.price))
        .concat(listToQuery(filters.preferredHotels, 'preferredHotels'))
        .join('&');

export const filtersToHotelApiQueryParams = (filters: Filters): string =>
    []
        .concat(hotelToQuery(filters.hotel))
        .concat(checkInCheckOutToQuery(filters.checkInDate, filters.checkOutDate))
        .concat(roomsToQuery(filters.rooms))
        .join('&');
